// Feature-detect a file's readability as an image, without ever throwing or
// rejecting. Extracted from the photo-upload component so it can be unit-tested
// in isolation (the component pulls in MUI / dropzone / contexts).
//
// Why it never rejects: it used to `reject(new Error("Error reading the file"))`
// on a read error, and that rejection escaped createThumbnail's async-Promise
// executor as an *unhandled* rejection reported to Sentry (FOTO-NEXTJS-12T). A
// file we can't read is simply "not readable" → resolve(false). Likewise, some
// in-app WebViews (Facebook, older iOS Chrome) don't expose FileReader at all
// (FOTO-NEXTJS-12P "Can't find variable: FileReader"); we fall back to an
// object URL there instead of crashing.
export async function isImageReadable(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const testSrc = (src: string, cleanup?: () => void) => {
      const img = new Image();
      img.onload = () => {
        cleanup?.();
        resolve(true);
      };
      img.onerror = () => {
        cleanup?.();
        resolve(false);
      };
      img.src = src;
    };

    if (typeof FileReader === "undefined") {
      try {
        const url = URL.createObjectURL(file);
        testSrc(url, () => URL.revokeObjectURL(url));
      } catch {
        resolve(false);
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) =>
      testSrc(e.target?.result as string);
    reader.onerror = () => resolve(false);
    try {
      reader.readAsDataURL(file);
    } catch {
      resolve(false);
    }
  });
}
